import { useEffect, useState } from "react";
import { styled } from "@pigment-css/react";
import { Checkmark, ChevronLeft } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { MeetingCancellationReason } from "@acto/gql/graphql";
import { FullScreenSheet } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { Card } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { GlassButton } from "@acto/taskpane/field-sales-2/components/primitives/GlassButton";
import { CtaRow, SolidCtaButton } from "@acto/taskpane/field-sales-2/components/primitives/SheetCta";
import {
  OptionButton,
  OptionsList,
  RadioCircle,
  SheetQuestion,
} from "@acto/taskpane/field-sales-2/components/primitives/SheetOptions";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import {
  BucketBody,
  BucketCard,
  ListCard,
  RecapCardHeader,
  RecapCardLabel,
  type RecapRow,
} from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapList";

const REASONS_IN_DISPLAY_ORDER = [
  MeetingCancellationReason.Sick,
  MeetingCancellationReason.Vacation,
  MeetingCancellationReason.DidNotHappen,
  MeetingCancellationReason.Other,
] as const;

const INITIAL_SELECTION_COUNT = 2;
const SELECTION_PAGE_SIZE = 10;
const SHEET_HEADER_HEIGHT = 82;

const CardWrap = styled("div")(({ theme }) => ({
  paddingInline: theme.fieldSalesTwo.spacing[16],
}));

const List = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const ReasonHeading = styled(SheetQuestion)(({ theme }) => ({
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[20]} ${theme.fieldSalesTwo.spacing[8]}`,
  marginBottom: theme.fieldSalesTwo.spacing[24],
  textAlign: "center",
}));

const StaticRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[12],
  width: "100%",
  padding: `${theme.fieldSalesTwo.spacing[8]} 0`,
  boxSizing: "border-box",
  fontFamily: theme.fieldSalesTwo.font.family,
}));

const SelectRow = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
  padding: `${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[8]} ${theme.fieldSalesTwo.spacing[8]}`,
  boxSizing: "border-box",
  borderRadius: theme.fieldSalesTwo.radius[24],
  border: "none",
  background: "transparent",
  textAlign: "left",
  fontFamily: theme.fieldSalesTwo.font.family,
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  ...pressInteraction(theme, { scale: 1 }),
  "&:disabled": { opacity: 0.4, pointerEvents: "none" },
}));

const DateBadge = styled("div")(({ theme }) => ({
  flex: "0 0 auto",
  width: 56,
  height: 52,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.fieldSalesTwo.radius[18],
  background: "rgba(19, 19, 19, 0.04)",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const RowBody = styled("span")({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
});

const RowTitle = styled("span")(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size[16],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const RowMeta = styled("span")(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

// The sheet's list card hugs its rows closer than the shared ListCard default: the rows'
// own 8px padding completes the design's 24/20 card inset and 16px row rhythm.
const SheetListCard = styled(ListCard)(({ theme }) => ({
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[12]} ${theme.fieldSalesTwo.spacing[12]}`,
}));

const ReasonCard = styled(Card)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[20]} ${theme.fieldSalesTwo.spacing[20]}`,
}));

const CheckCircle = styled("span")(({ theme }) => ({
  flexShrink: 0,
  width: "22px",
  height: "22px",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  // Squircle, not a full circle: radius[8] on the 22px box, like the TodoListItem check.
  borderRadius: theme.fieldSalesTwo.radius[8],
  border: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[300]}`,
  color: "transparent",
  transition: "background-color 120ms ease, border-color 120ms ease",
  '[data-state="checked"] > &': {
    borderColor: theme.fieldSalesTwo.colors.accent.action,
    backgroundColor: theme.fieldSalesTwo.colors.accent.action,
    color: theme.fieldSalesTwo.colors.neutral[50],
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

const ExpandButton = styled(GlassButton)({
  width: "100%",
});

const ReasonOptions = styled(OptionsList)(({ theme }) => ({
  marginTop: theme.fieldSalesTwo.spacing[24],
}));

const CommentField = styled("textarea")(({ theme }) => ({
  minHeight: "7.5rem",
  marginTop: theme.fieldSalesTwo.spacing[8],
  marginInline: theme.fieldSalesTwo.spacing[16],
  padding: theme.fieldSalesTwo.spacing[20],
  boxSizing: "border-box",
  resize: "none",
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.input,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  "&::placeholder": { color: theme.fieldSalesTwo.colors.neutral[700] },
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: `-${theme.strokeWidth.regular}`,
  },
  "&:disabled": { opacity: 0.4, pointerEvents: "none" },
}));

// Stays solid behind both the CTA and the confirm note beneath it; the 80px on top is the fade zone.
const DeleteCtaRow = styled(CtaRow)(({ theme }) => ({
  paddingTop: "80px",
  background: `linear-gradient(to top, ${theme.fieldSalesTwo.colors.background} 67.43%, rgba(240,238,237,0))`,
}));

const ConfirmNote = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[700],
  textAlign: "center",
}));

interface AdjustReportsOverlayProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Open reports only — a completed report is never cancellable. */
  rows: RecapRow[];
  onConfirm: (ids: string[], reason: MeetingCancellationReason, comment?: string) => void;
  isSubmitting?: boolean;
}

function useHeadingInView(active: boolean) {
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const [headingEl, setHeadingEl] = useState<HTMLHeadingElement | null>(null);
  const [headingInView, setHeadingInView] = useState(false);

  useEffect(() => {
    if (!active || !bodyEl || !headingEl || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setHeadingInView(entry.isIntersecting), {
      root: bodyEl,
      rootMargin: `-${SHEET_HEADER_HEIGHT}px 0px 0px 0px`,
    });
    observer.observe(headingEl);
    return () => {
      observer.disconnect();
      setHeadingInView(false);
    };
  }, [active, bodyEl, headingEl]);

  return { setBodyEl, setHeadingEl, headingInView };
}

/**
 * Cancels open visit reports in two steps inside one overlay: pick the reports, then pick the single
 * reason that applies to all of them. Presentational — the caller owns the mutation.
 */
export function AdjustReportsOverlay({
  isOpen,
  onOpenChange,
  rows,
  onConfirm,
  isSubmitting,
}: AdjustReportsOverlayProps) {
  const t = useTranslations("fieldSalesTwo.recaps.adjust");
  const [step, setStep] = useState<"select" | "reason">("select");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [reason, setReason] = useState<MeetingCancellationReason | null>(null);
  const [comment, setComment] = useState("");
  const [visibleCount, setVisibleCount] = useState(INITIAL_SELECTION_COUNT);
  const { setBodyEl, setHeadingEl, headingInView } = useHeadingInView(step === "reason");

  useEffect(() => {
    if (!isOpen) {
      setStep("select");
      setSelectedIds([]);
      setReason(null);
      setComment("");
      setVisibleCount(INITIAL_SELECTION_COUNT);
    }
  }, [isOpen]);

  const selectedRows = rows.filter((row) => selectedIds.includes(row.id));
  const trimmedComment = comment.trim();
  const commentRequired = reason === MeetingCancellationReason.Other;
  const canConfirm = reason !== null && (!commentRequired || trimmedComment !== "");

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    if (step === "reason") {
      setStep("select");
      return;
    }
    onOpenChange(false);
  };

  const toggle = (id: string) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((selected) => selected !== id) : [...current, id],
    );

  const handleConfirm = () => {
    if (!reason || !canConfirm) return;
    onConfirm(selectedIds, reason, trimmedComment === "" ? undefined : trimmedComment);
  };

  const renderRow = (row: RecapRow, checked: boolean, onClick?: () => void) => (
    <SelectRow
      key={row.id}
      type="button"
      role="checkbox"
      aria-checked={checked}
      data-state={checked ? "checked" : "unchecked"}
      disabled={isSubmitting}
      onClick={onClick}
    >
      <DateBadge>
        <span>{row.badge.primary}</span>
        {row.badge.secondary && <span>{row.badge.secondary}</span>}
      </DateBadge>
      <RowBody>
        <RowTitle>{row.name}</RowTitle>
        <RowMeta>{row.address}</RowMeta>
      </RowBody>
      <CheckCircle aria-hidden="true">
        <Checkmark width={12} height={12} />
      </CheckCircle>
    </SelectRow>
  );

  const visibleSelection = selectedRows.slice(0, visibleCount);
  const hiddenSelectionCount = selectedRows.length - visibleSelection.length;
  const revealMore = () => setVisibleCount((count) => Math.min(count + SELECTION_PAGE_SIZE, selectedRows.length));

  return (
    <FullScreenSheet
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      title={step === "select" ? t("selectTitle") : t("reasonTitle")}
      closeAriaLabel={step === "select" ? t("close") : t("back")}
      closeIcon={step === "reason" ? <ChevronLeft /> : undefined}
      bodyRef={setBodyEl}
      titleHidden={step === "reason" && headingInView}
    >
      {step === "select" ? (
        <>
          <CardWrap>
            <BucketCard>
              <RecapCardHeader>
                <RecapCardLabel as="h2">{t("openReports")}</RecapCardLabel>
              </RecapCardHeader>
              <BucketBody>
                <SheetListCard>
                  <List>{rows.map((row) => renderRow(row, selectedIds.includes(row.id), () => toggle(row.id)))}</List>
                </SheetListCard>
              </BucketBody>
            </BucketCard>
          </CardWrap>
          <DeleteCtaRow>
            {selectedIds.length === 0 ? (
              <SolidCtaButton type="button" onClick={() => onOpenChange(false)}>
                {t("abort")}
              </SolidCtaButton>
            ) : (
              <SolidCtaButton type="button" onClick={() => setStep("reason")}>
                {t("cancelCta", { count: selectedIds.length })}
              </SolidCtaButton>
            )}
          </DeleteCtaRow>
        </>
      ) : (
        <>
          <ReasonHeading ref={setHeadingEl}>{t("reasonQuestion")}</ReasonHeading>
          <CardWrap>
            <ReasonCard>
              <List>
                {visibleSelection.map((row) => (
                  <StaticRow key={row.id}>
                    <DateBadge>
                      <span>{row.badge.primary}</span>
                      {row.badge.secondary && <span>{row.badge.secondary}</span>}
                    </DateBadge>
                    <RowBody>
                      <RowTitle>{row.name}</RowTitle>
                      <RowMeta>{row.address}</RowMeta>
                    </RowBody>
                  </StaticRow>
                ))}
              </List>
              {selectedRows.length > INITIAL_SELECTION_COUNT &&
                (hiddenSelectionCount > 0 ? (
                  <ExpandButton type="button" disabled={isSubmitting} onClick={revealMore}>
                    {hiddenSelectionCount <= SELECTION_PAGE_SIZE
                      ? t("showMore", { count: hiddenSelectionCount })
                      : t("showMoreBatch")}
                  </ExpandButton>
                ) : (
                  <ExpandButton
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => setVisibleCount(INITIAL_SELECTION_COUNT)}
                  >
                    {t("showLess")}
                  </ExpandButton>
                ))}
            </ReasonCard>
          </CardWrap>
          <ReasonOptions
            value={reason ?? undefined}
            onValueChange={(value) => setReason(value as MeetingCancellationReason)}
            aria-label={t("reasonQuestion")}
          >
            {REASONS_IN_DISPLAY_ORDER.map((option) => (
              <OptionButton key={option} value={option} disabled={isSubmitting}>
                {t(`reason.${option}`)}
                <RadioCircle aria-hidden="true" />
              </OptionButton>
            ))}
          </ReasonOptions>
          <CommentField
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={commentRequired ? t("commentPlaceholderRequired") : t("commentPlaceholder")}
            disabled={isSubmitting}
          />
          <DeleteCtaRow>
            <SolidCtaButton type="button" disabled={!canConfirm || isSubmitting} onClick={handleConfirm}>
              {t("cancelCta", { count: selectedIds.length })}
            </SolidCtaButton>
            <ConfirmNote>{t("confirmNote")}</ConfirmNote>
          </DeleteCtaRow>
        </>
      )}
    </FullScreenSheet>
  );
}
